import type { FeatureMetadataRow } from './feature-metadata.models';
import {
  contributingFactorsCaptainRosterForSlots,
  type VoyageContributingFactorRow,
  type VoyageContributingFactorsVoyage,
  type VoyageContributingPerCaptainNamed,
} from './voyage-contributing-factors.models';

const CAPTAIN_CATEGORY = 'Factors - Captain';

export type ContributingFactorUiKind = 'slider' | 'dropdown' | 'bool' | 'time' | 'month' | 'year' | 'text' | 'hidden';

export interface ContributingFactorsFormPatchContext {
  getFactorUiKind: (row: FeatureMetadataRow) => ContributingFactorUiKind;
  factorFormKey: (row: FeatureMetadataRow) => string;
  captainDropdownOptions: ReadonlyArray<{ id: unknown; name: unknown }>;
  manningDropdownOptions: ReadonlyArray<{ id: unknown; name: unknown }>;
  slotCaptainKey: string;
  slotManningKey: string;
}

function indexFactorsByFeatureId(
  voyage: VoyageContributingFactorsVoyage,
): Map<number, VoyageContributingFactorRow> {
  const m = new Map<number, VoyageContributingFactorRow>();
  const b = voyage.factors;
  const all = [
    ...(b.captain ?? []),
    ...(b.crew ?? []),
    ...(b.ship ?? []),
    ...(b.voyage ?? []),
  ];
  for (const row of all) {
    m.set(row.featureId, row);
  }
  return m;
}

function parseScalarNumber(apiRow: VoyageContributingFactorRow): number | null {
  if (apiRow.scalarValueUi != null && String(apiRow.scalarValueUi).trim() !== '') {
    const n = Number(String(apiRow.scalarValueUi).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  const a = apiRow.aggregateValue;
  if (typeof a === 'number' && !Number.isNaN(a)) {
    return a;
  }
  return null;
}

function isNamedEntry(v: unknown): v is VoyageContributingPerCaptainNamed {
  return (
    v != null &&
    typeof v === 'object' &&
    'name' in v &&
    typeof (v as { name: unknown }).name === 'string'
  );
}

function optionIdByNameOrId(
  options: ReadonlyArray<{ id: unknown; name: unknown }>,
  raw: string,
): string | null {
  const t = String(raw ?? '').trim();
  if (!t) {
    return null;
  }
  const byId = options.find((o) => String(o.id) === t);
  if (byId) {
    return String(byId.id);
  }
  const tl = t.toLowerCase();
  const byName = options.find(
    (o) => String(o.name ?? '').trim().toLowerCase() === tl,
  );
  if (byName) {
    return String(byName.id);
  }
  const byIncludes = options.find((o) => {
    const n = String(o.name ?? '').trim().toLowerCase();
    return n.length > 0 && (tl.includes(n) || n.includes(tl));
  });
  return byIncludes ? String(byIncludes.id) : null;
}

function manningIdsFromLabel(
  label: string,
  options: ReadonlyArray<{ id: unknown; name: unknown }>,
): string[] {
  const id = optionIdByNameOrId(options, label);
  return id ? [id] : [];
}

/** Slider stores raw counts / scores; API may send a fractional ratio in (0, 1) — scale to 0–100 only then. */
const SLIDER_UI_MAX = 1_000_000;

/** Catalogue `rangeMax` ≤ 1 — slider UI uses the same decimal domain as the API (e.g. 0.24). */
export function factorSliderUsesDecimalUi(factor: FeatureMetadataRow): boolean {
  const hi = factor.rangeMax;
  return hi != null && Number.isFinite(hi) && hi <= 1;
}

/** API scalar is a fractional ratio in (0, 1) — never scale to 0–100 in the slider. */
export function apiScalarUsesDecimalSliderUi(n: number): boolean {
  return Number.isFinite(n) && n > 0 && n < 1;
}

function decimalSliderClamp(n: number, factor?: FeatureMetadataRow): number {
  const lo = factor?.rangeMin ?? 0;
  const hi =
    factor?.rangeMax != null && Number.isFinite(factor.rangeMax) && factor.rangeMax <= 1
      ? factor.rangeMax
      : 1;
  const clamped = Math.min(hi, Math.max(lo, n));
  return Math.round(clamped * 100) / 100;
}

/** Maps API scalar → slider `FormControl` value (preserves up to 2 decimals, e.g. 26.15). */
export function scalarSliderFromApi(n: number, factor?: FeatureMetadataRow): number {
  if (!Number.isFinite(n)) {
    return 0;
  }
  if (
    (factor && factorSliderUsesDecimalUi(factor)) ||
    apiScalarUsesDecimalSliderUi(n)
  ) {
    return decimalSliderClamp(n, factor);
  }
  const clamped = Math.min(SLIDER_UI_MAX, Math.max(0, n));
  // Match `formatContributingFactorNumericDisplay` (max 2 decimal places), not integer rounding.
  return Math.round(clamped * 100) / 100;
}

/** Maps slider `FormControl` value → API scalar (inverse of `scalarSliderFromApi`). */
export function scalarSliderToApi(
  ui: number,
  factor: FeatureMetadataRow,
  originalAggregate?: number | null,
): number {
  if (!Number.isFinite(ui)) {
    return 0;
  }
  if (factorSliderUsesDecimalUi(factor) || apiScalarUsesDecimalSliderUi(ui)) {
    return decimalSliderClamp(ui, factor);
  }
  // Legacy rows hydrated before decimal fix may still hold 0–100 in the form (e.g. 24 for 0.24).
  if (typeof originalAggregate === 'number' && originalAggregate > 0 && originalAggregate < 1 && ui > 1) {
    return Math.min(1, Math.max(0, ui / 100));
  }
  return ui;
}

function valueForUiKind(
  kind: ContributingFactorUiKind,
  n: number | null,
  textFallback: string,
  meta: FeatureMetadataRow,
): unknown {
  if (kind === 'slider') {
    return scalarSliderFromApi(n ?? 0, meta);
  }
  if (kind === 'time') {
    const days = n ?? 0;
    return Math.max(0, Math.round(days));
  }
  if (kind === 'bool') {
    const v = n ?? 0;
    return v !== 0 && !Number.isNaN(v);
  }
  if (kind === 'dropdown') {
    return [];
  }
  return textFallback;
}

function perCaptainNumeric(
  apiRow: VoyageContributingFactorRow,
  captainId: string,
): number | null {
  const entry = apiRow.perCaptain?.[captainId];
  if (entry && typeof entry === 'object' && 'value' in entry) {
    const v = (entry as { value: unknown }).value;
    if (typeof v === 'number' && !Number.isNaN(v)) {
      return v;
    }
  }
  if (apiRow.shape === 'scalar') {
    return parseScalarNumber(apiRow);
  }
  return null;
}

function perCaptainDropdownIds(
  apiRow: VoyageContributingFactorRow,
  captainId: string,
  meta: FeatureMetadataRow,
  ctx: ContributingFactorsFormPatchContext,
): string[] | undefined {
  const entry = apiRow.perCaptain?.[captainId];
  if (!isNamedEntry(entry)) {
    return undefined;
  }
  const name = entry.name;
  const ui = meta.uiName.trim().toLowerCase();
  const isCaptainLike =
    ui === 'captain' || meta.apiName.toLowerCase().includes('captaincrew');
  const isManningLike =
    ui === 'manning agent' || meta.apiName.toLowerCase().includes('manning');
  if (isCaptainLike) {
    const id = optionIdByNameOrId(ctx.captainDropdownOptions, name);
    return id ? [id] : [];
  }
  if (isManningLike) {
    const id = optionIdByNameOrId(ctx.manningDropdownOptions, name);
    return id ? [id] : [];
  }
  const id =
    optionIdByNameOrId(ctx.captainDropdownOptions, name) ??
    optionIdByNameOrId(ctx.manningDropdownOptions, name);
  return id ? [id] : [];
}

function patchForCaptainSlot(
  apiRow: VoyageContributingFactorRow,
  captainId: string,
  meta: FeatureMetadataRow,
  ctx: ContributingFactorsFormPatchContext,
): unknown {
  const kind = ctx.getFactorUiKind(meta);
  if (apiRow.shape === 'perCaptainNamed' && kind === 'dropdown') {
    return perCaptainDropdownIds(apiRow, captainId, meta, ctx);
  }
  const n = perCaptainNumeric(apiRow, captainId);
  const text =
    apiRow.scalarValueUi != null && String(apiRow.scalarValueUi).trim() !== ''
      ? String(apiRow.scalarValueUi)
      : n != null && Number.isFinite(n)
        ? String(n)
        : '';
  if (kind === 'dropdown') {
    return n != null ? [] : [];
  }
  return valueForUiKind(kind, n, text, meta);
}

function patchForRootScalar(
  apiRow: VoyageContributingFactorRow,
  meta: FeatureMetadataRow,
  ctx: ContributingFactorsFormPatchContext,
): unknown {
  const kind = ctx.getFactorUiKind(meta);
  const n = parseScalarNumber(apiRow);
  const text =
    apiRow.scalarValueUi != null && String(apiRow.scalarValueUi).trim() !== ''
      ? String(apiRow.scalarValueUi)
      : n != null && Number.isFinite(n)
        ? String(n)
        : '';
  if (kind === 'dropdown') {
    const id = optionIdByNameOrId(ctx.captainDropdownOptions, text);
    if (id) {
      return [id];
    }
    const idM = optionIdByNameOrId(ctx.manningDropdownOptions, text);
    return idM ? [idM] : [];
  }
  return valueForUiKind(kind, n, text, meta);
}

/**
 * Builds a partial `FormGroup` value (same shape as `adjustableFactors` voyage group) from
 * GET `/api/voyage-contributing-factors/:shipCode` voyage payload.
 */
export function buildContributingFactorsFormPatch(
  voyage: VoyageContributingFactorsVoyage,
  metadataRows: FeatureMetadataRow[],
  ctx: ContributingFactorsFormPatchContext,
): Record<string, unknown> {
  const apiByFeature = indexFactorsByFeatureId(voyage);
  const rows = metadataRows.filter((r) => r.isActive && r.isEditable);
  const captainMeta = rows.filter((r) => r.category.trim() === CAPTAIN_CATEGORY);
  const nonCaptainMeta = rows.filter((r) => r.category.trim() !== CAPTAIN_CATEGORY);

  const roster = contributingFactorsCaptainRosterForSlots(voyage);
  const patch: Record<string, unknown> = {};

  if (captainMeta.length > 0 && roster.length > 0) {
    const captainsArr: Record<string, unknown>[] = [];
    for (let i = 0; i < roster.length; i++) {
      const cap = roster[i];
      const captainId = String(cap.captainId ?? '');
      const slot: Record<string, unknown> = {
        [ctx.slotCaptainKey]: captainId ? [captainId] : [],
        [ctx.slotManningKey]: manningIdsFromLabel(cap.manningAgent ?? '', ctx.manningDropdownOptions),
      };
      for (const meta of captainMeta) {
        const apiRow = apiByFeature.get(meta.featureId);
        if (!apiRow) {
          continue;
        }
        const fk = ctx.factorFormKey(meta);
        const patched = patchForCaptainSlot(apiRow, captainId, meta, ctx);
        if (patched !== undefined) {
          slot[fk] = patched;
        }
      }
      captainsArr.push(slot);
    }
    patch['captains'] = captainsArr;
  }

  for (const meta of nonCaptainMeta) {
    const apiRow = apiByFeature.get(meta.featureId);
    if (!apiRow || apiRow.shape !== 'scalar') {
      continue;
    }
    const fk = ctx.factorFormKey(meta);
    patch[fk] = patchForRootScalar(apiRow, meta, ctx);
  }

  return patch;
}
